import { Controller } from "@hotwired/stimulus"

export default class extends Controller {
  static targets = ["panel"]

  connect() {
    document.addEventListener("keydown", this.handleKeydown.bind(this))
  }

  disconnect() {
    document.removeEventListener("keydown", this.handleKeydown.bind(this))
  }

  handleKeydown(event) {
    if ((event.metaKey || event.ctrlKey) && event.key === "k") {
      event.preventDefault()
      this.open()
    }
    if (event.key === "Escape") {
      this.close()
    }
  }

  open() {
    const panel = document.getElementById("quick-info-panel")
    if (panel) {
      panel.classList.remove("hidden")
      this.renderHelpContent()
    }
  }

  close() {
    const panel = document.getElementById("quick-info-panel")
    if (panel) {
      panel.classList.add("hidden")
    }
  }

  renderHelpContent() {
    const panel = document.getElementById("quick-info-panel")
    const contentArea = panel?.querySelector("[data-prt-quick-info-target='panel']")
    if (!contentArea) return

    contentArea.innerHTML = this.getHelpHTML()
    this.showTab("dashboard")
  }

  showTab(tabId) {
    const contents = document.querySelectorAll("[data-help-tab-content]")
    contents.forEach(c => c.classList.add("hidden"))

    const selectedContent = document.getElementById(`help-${tabId}`)
    if (selectedContent) selectedContent.classList.remove("hidden")

    const buttons = document.querySelectorAll("[data-help-tab]")
    buttons.forEach(btn => {
      const isSelected = btn.dataset.helpTab === tabId
      btn.className = isSelected
        ? "flex items-center px-3 py-2 text-sm font-medium text-[#C4A484] bg-[#C4A484]/10 rounded-lg"
        : "flex items-center px-3 py-2 text-sm text-[#8C8C8C] hover:bg-[#F5F3F0] rounded-lg transition-colors"
    })
  }

  getHelpHTML() {
    return `
      <div class="h-full flex flex-col">
        <div class="p-6 border-b border-[#E8E4DF]">
          <div class="flex items-center justify-between">
            <div>
              <h2 class="text-lg font-semibold text-[#2D2D2D]">Help & Documentation</h2>
              <p class="text-sm text-[#8C8C8C] mt-1">Learn how to use PRT Mission Control</p>
            </div>
            <button onclick="document.getElementById('quick-info-panel')?.classList.add('hidden')" class="text-[#8C8C8C] hover:text-[#2D2D2D] transition-colors">
              <svg class="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M6 18L18 6M6 6l12 12"/>
              </svg>
            </button>
          </div>
        </div>
        <div class="flex flex-1 overflow-hidden">
          <div class="w-44 border-r border-[#E8E4DF] p-3 overflow-y-auto">
            <nav class="space-y-1">${this.getTabButtons()}</nav>
          </div>
          <div class="flex-1 overflow-y-auto p-6">
            ${this.getDashboardHelp()}
            ${this.getCalendarHelp()}
            ${this.getInboxHelp()}
            ${this.getClientsHelp()}
            ${this.getDpkHelp()}
            ${this.getReleasesHelp()}
            ${this.getTasksHelp()}
            ${this.getInternsHelp()}
            ${this.getReviewQueueHelp()}
            ${this.getContactsHelp()}
            ${this.getOutletsHelp()}
            ${this.getAiSearchHelp()}
            ${this.getNotificationsHelp()}
            ${this.getTeamHelp()}
            ${this.getFilesHelp()}
            ${this.getLogHistoryHelp()}
            ${this.getUsersHelp()}
            ${this.getImportHelp()}
            ${this.getProfileHelp()}
            ${this.getSettingsHelp()}
            ${this.getShortcutsHelp()}
          </div>
        </div>
      </div>
    `
  }

  getTabButtons() {
    const tabs = [
      { id: "dashboard", icon: this.iconGrid(), label: "Dashboard" },
      { id: "calendar", icon: this.iconCalendar(), label: "Calendar" },
      { id: "inbox", icon: this.iconInbox(), label: "Inbox", badge: "Coming Soon" },
      { id: "clients", icon: this.iconClients(), label: "Clients" },
      { id: "dpk", icon: this.iconDpk(), label: "DPKs" },
      { id: "releases", icon: this.iconReleases(), label: "Releases" },
      { id: "tasks", icon: this.iconTasks(), label: "Tasks" },
      { id: "interns", icon: this.iconInterns(), label: "Interns" },
      { id: "reviewqueue", icon: this.iconReviewQueue(), label: "Review Queue" },
      { id: "contacts", icon: this.iconContacts(), label: "Contacts" },
      { id: "outlets", icon: this.iconOutlets(), label: "Outlets" },
      { id: "aisearch", icon: this.iconAiSearch(), label: "AI Search" },
      { id: "notifications", icon: this.iconNotifications(), label: "Notifications" },
      { id: "team", icon: this.iconTeam(), label: "TREND Team" },
      { id: "files", icon: this.iconFiles(), label: "Files" },
      { id: "loghistory", icon: this.iconLogs(), label: "Log History" },
      { id: "users", icon: this.iconUsers(), label: "Users" },
      { id: "import", icon: this.iconImport(), label: "Import" },
      { id: "profile", icon: this.iconProfile(), label: "Profile" },
      { id: "settings", icon: this.iconSettings(), label: "Settings" },
      { id: "shortcuts", icon: this.iconShortcuts(), label: "Shortcuts" },
    ]
    return tabs.map((tab, i) => {
      const badgeHtml = tab.badge ? `<span class="ml-1 text-[9px] text-[#C4A484]">${tab.badge}</span>` : ""
      return `
        <button data-help-tab="${tab.id}" onclick="window.showHelpTab('${tab.id}')" class="${i === 0 ? 'flex items-center px-3 py-2 text-sm font-medium text-[#C4A484] bg-[#C4A484]/10 rounded-lg' : 'flex items-center px-3 py-2 text-sm text-[#8C8C8C] hover:bg-[#F5F3F0] rounded-lg transition-colors'}">
          ${tab.icon}<span class="ml-2">${tab.label}</span>${badgeHtml}
        </button>
      `
    }).join("")
  }

  // ── Help Content Methods ──

  getDashboardHelp() {
    return `
      <div id="help-dashboard" data-help-tab-content class="space-y-4">
        <h3 class="text-lg font-semibold text-[#2D2D2D]">Dashboard</h3>
        <p class="text-sm text-[#5A5A5A]">Your central hub for PR activities and campaign management.</p>
        <div class="space-y-3">
          <div class="p-3 bg-[#F5F3F0] rounded-lg">
            <h4 class="font-medium text-[#2D2D2D] text-sm">Quick Stats</h4>
            <p class="text-xs text-[#8C8C8C] mt-1">Overview of total contacts, verified emails, active clients, and current releases.</p>
          </div>
          <div class="p-3 bg-[#F5F3F0] rounded-lg">
            <h4 class="font-medium text-[#2D2D2D] text-sm">Recent Activity</h4>
            <p class="text-xs text-[#8C8C8C] mt-1">Live feed of recent actions across PRT: pitch status changes, new contacts added, releases created, and more. See what your team has been working on.</p>
          </div>
          <div class="p-3 bg-[#F5F3F0] rounded-lg">
            <h4 class="font-medium text-[#2D2D2D] text-sm">My Tasks</h4>
            <p class="text-xs text-[#8C8C8C] mt-1">View your pending tasks ordered by due date. Click any to view details or mark complete.</p>
          </div>
          <div class="p-3 bg-[#F5F3F0] rounded-lg">
            <h4 class="font-medium text-[#2D2D2D] text-sm">Review Queue Widget</h4>
            <p class="text-xs text-[#8C8C8C] mt-1">For admins and senior staff: a quick glance at items waiting for review (new outlets and email problems) without leaving the dashboard.</p>
          </div>
          <div class="p-3 bg-[#F5F3F0] rounded-lg">
            <h4 class="font-medium text-[#2D2D2D] text-sm">Upcoming Releases</h4>
            <p class="text-xs text-[#8C8C8C] mt-1">Releases scheduled for the next 30 days. Plan your pitching strategy accordingly.</p>
          </div>
        </div>
        <div class="p-3 border border-[#C4A484] rounded-lg bg-[#C4A484]/5">
          <p class="text-xs text-[#5A5A5A]"><strong>Pro Tip:</strong> Press <kbd class="px-1.5 py-0.5 bg-[#E8E4DF] rounded text-xs">Cmd/Ctrl + K</kbd> to open this help panel from anywhere.</p>
        </div>
      </div>
    `
  }

  getCalendarHelp() {
    return `
      <div id="help-calendar" data-help-tab-content class="hidden space-y-4">
        <h3 class="text-lg font-semibold text-[#2D2D2D]">Calendar</h3>
        <p class="text-sm text-[#5A5A5A]">Visualize releases and tasks on a calendar view.</p>
        <div class="space-y-3">
          <div class="p-3 bg-[#F5F3F0] rounded-lg">
            <h4 class="font-medium text-[#2D2D2D] text-sm">Calendar Items</h4>
            <ul class="text-xs text-[#8C8C8C] mt-1 space-y-1 list-disc list-inside">
              <li>Release dates for all clients</li>
              <li>Task due dates</li>
              <li>Pitch deadlines</li>
            </ul>
          </div>
        </div>
      </div>
    `
  }

  getInboxHelp() {
    return `
      <div id="help-inbox" data-help-tab-content class="hidden space-y-4">
        <div class="flex items-center gap-2">
          <h3 class="text-lg font-semibold text-[#2D2D2D]">Inbox</h3>
          <span class="text-[10px] font-medium text-[#C4A484] bg-[#C4A484]/10 rounded-full px-2 py-0.5">Coming Soon</span>
        </div>
        <p class="text-sm text-[#5A5A5A]">Built-in email for pitching: coming soon.</p>
        <div class="space-y-3">
          <div class="p-3 bg-[#C4A484]/5 border border-[#C4A484]/20 rounded-lg">
            <h4 class="font-medium text-[#2D2D2D] text-sm">What&rsquo;s Coming</h4>
            <p class="text-xs text-[#8C8C8C] mt-1">The Inbox will be the home for all pitch email communication, replacing HubSpot and Google Workspace for sending pitches, follow-ups, and press releases. You'll be able to email journalists directly from PRT without switching apps.</p>
          </div>
          <div class="p-3 bg-[#F5F3F0] rounded-lg">
            <h4 class="font-medium text-[#2D2D2D] text-sm">Planned Features</h4>
            <ul class="text-xs text-[#8C8C8C] mt-1 space-y-1 list-disc list-inside">
              <li>Send personalized pitch emails from within a release's press distribution list</li>
              <li>Use templates for common pitch types</li>
              <li>Schedule emails for optimal send times</li>
              <li>Track opens and responses</li>
              <li>Full email thread history for every contact</li>
              <li>When a journalist replies, the conversation shows up here tied to the contact and release</li>
            </ul>
          </div>
          <div class="p-3 bg-[#F5F3F0] rounded-lg">
            <p class="text-xs text-[#8C8C8C]">This feature is currently in development. You'll see "Coming Soon" next to Inbox in the sidebar until it's ready.</p>
          </div>
        </div>
      </div>
    `
  }

  getClientsHelp() {
    return `
      <div id="help-clients" data-help-tab-content class="hidden space-y-4">
        <h3 class="text-lg font-semibold text-[#2D2D2D]">Clients</h3>
        <p class="text-sm text-[#5A5A5A]">Manage music artists and their PR campaigns.</p>
        <div class="space-y-3">
          <div class="p-3 bg-[#F5F3F0] rounded-lg">
            <h4 class="font-medium text-[#2D2D2D] text-sm">Client Profiles</h4>
            <p class="text-xs text-[#8C8C8C] mt-1">Each client has name, bio, genre, social links, and a primary publicist assignment.</p>
          </div>
          <div class="p-3 bg-[#F5F3F0] rounded-lg">
            <h4 class="font-medium text-[#2D2D2D] text-sm">Creating Releases</h4>
            <p class="text-xs text-[#8C8C8C] mt-1">From a client's page, create releases (singles, EPs, albums). Each release becomes a separate press campaign.</p>
          </div>
          <div class="p-3 bg-[#F5F3F0] rounded-lg">
            <h4 class="font-medium text-[#2D2D2D] text-sm">Primary Publicist</h4>
            <p class="text-xs text-[#8C8C8C] mt-1">Assign a publicist to each client. This is visible in the Releases table so admins can see who manages each campaign.</p>
          </div>
        </div>
      </div>
    `
  }

  getDpkHelp() {
    return `
      <div id="help-dpk" data-help-tab-content class="hidden space-y-4">
        <h3 class="text-lg font-semibold text-[#2D2D2D]">Digital Press Kits (DPKs)</h3>
        <p class="text-sm text-[#5A5A5A]">DPKs are the public-facing press pages you share with journalists, managers, and labels. Each client can have a DPK hosted at music.trendpr.com.</p>
        <div class="space-y-3">
          <div class="p-3 bg-[#F5F3F0] rounded-lg">
            <h4 class="font-medium text-[#2D2D2D] text-sm">DPK Dashboard</h4>
            <p class="text-xs text-[#8C8C8C] mt-1">See all client DPKs at a glance with status badges (Active, Draft, Legacy). Click any tile to manage that client's DPK.</p>
          </div>
          <div class="p-3 bg-[#F5F3F0] rounded-lg">
            <h4 class="font-medium text-[#2D2D2D] text-sm">Profile & Social</h4>
            <p class="text-xs text-[#8C8C8C] mt-1">Edit the artist's bio, contact info, social media links, and quotes. Content auto-saves as you type. Pick a background photo for the public DPK page.</p>
          </div>
          <div class="p-3 bg-[#F5F3F0] rounded-lg">
            <h4 class="font-medium text-[#2D2D2D] text-sm">Albums & Music</h4>
            <p class="text-xs text-[#8C8C8C] mt-1">Add albums with cover art, track listings, and MP3 uploads. Each track gets a waveform player on the public page. Mark suggested tracks to highlight them.</p>
          </div>
          <div class="p-3 bg-[#F5F3F0] rounded-lg">
            <h4 class="font-medium text-[#2D2D2D] text-sm">Photos</h4>
            <p class="text-xs text-[#8C8C8C] mt-1">Upload press photos for the client. Set a main photo (used as the DPK hero image). Toggle photos between public and private. Drag to reorder.</p>
          </div>
          <div class="p-3 bg-[#F5F3F0] rounded-lg">
            <h4 class="font-medium text-[#2D2D2D] text-sm">Press Clips</h4>
            <p class="text-xs text-[#8C8C8C] mt-1">Create visual press clippings from article URLs. The clipping editor auto-fetches the article title, description, and image. Use the built-in editor to compose a square clipping image with custom fonts, text borders, drop shadows, and outlet logos. You can also pick background images from the client's DPK photos or paste any image URL. Clips can be linked to specific releases.</p>
          </div>
          <div class="p-3 bg-[#F5F3F0] rounded-lg">
            <h4 class="font-medium text-[#2D2D2D] text-sm">Videos</h4>
            <p class="text-xs text-[#8C8C8C] mt-1">Embed YouTube or Vimeo videos that display on the public DPK page.</p>
          </div>
          <div class="p-3 bg-[#F5F3F0] rounded-lg">
            <h4 class="font-medium text-[#2D2D2D] text-sm">Monthly Reports</h4>
            <p class="text-xs text-[#8C8C8C] mt-1">Generate monthly press reports for clients showing all press coverage secured during a date range. Reports include press clippings and can be shared via a unique URL.</p>
          </div>
        </div>
      </div>
    `
  }

  getReleasesHelp() {
    return `
      <div id="help-releases" data-help-tab-content class="hidden space-y-4">
        <h3 class="text-lg font-semibold text-[#2D2D2D]">Releases & Press Distribution</h3>
        <p class="text-sm text-[#5A5A5A]">Build and manage press distribution lists for each release campaign.</p>
        <div class="space-y-3">
          <div class="p-3 bg-[#F5F3F0] rounded-lg">
            <h4 class="font-medium text-[#2D2D2D] text-sm">Press Distribution List</h4>
            <p class="text-xs text-[#8C8C8C] mt-1">Each release has a press distribution list. Click "Start Press Distribution List" (new) or "Edit Press Distribution List" (existing) to add contacts via three methods:</p>
            <ul class="text-xs text-[#8C8C8C] mt-1 space-y-1 list-disc list-inside">
              <li><strong>Database Search:</strong> Search contacts AND outlets by name. Searching an outlet shows all its writers so you can pick who to add.</li>
              <li><strong>Manual Add:</strong> Create a new contact on the fly. System detects if outlet or contact already exists.</li>
              <li><strong>PR Research:</strong> Discover new outlets via Google search.</li>
            </ul>
          </div>
          <div class="p-3 bg-[#F5F3F0] rounded-lg">
            <h4 class="font-medium text-[#2D2D2D] text-sm">Pitch Status (Traffic Lights)</h4>
            <ul class="text-xs text-[#8C8C8C] mt-1 space-y-1">
              <li><span class="inline-block w-2.5 h-2.5 rounded-full bg-[#D1D5DB] mr-1"></span> <strong>Not Pitched</strong> - Not reached yet</li>
              <li><span class="inline-block w-2.5 h-2.5 rounded-full bg-[#FFB74D] mr-1"></span> <strong>Pitched / Following Up</strong> - Email sent</li>
              <li><span class="inline-block w-2.5 h-2.5 rounded-full bg-[#9CAF88] mr-1"></span> <strong>Confirmed</strong> - Coverage confirmed</li>
              <li><span class="inline-block w-2.5 h-2.5 rounded-full bg-[#E57373] mr-1"></span> <strong>Declined</strong> - Contact passed</li>
              <li><span class="inline-block w-2.5 h-2.5 rounded-full bg-[#8C8C8C] mr-1"></span> <strong>No Response</strong> - No reply</li>
            </ul>
          </div>
          <div class="p-3 bg-[#F5F3F0] rounded-lg">
            <h4 class="font-medium text-[#2D2D2D] text-sm">Quick Actions</h4>
            <ul class="text-xs text-[#8C8C8C] mt-1 space-y-1 list-disc list-inside">
              <li>Click the <strong>status dot</strong> to change pitch status</li>
              <li>Use <strong>checkboxes + bulk bar</strong> to update multiple contacts at once</li>
              <li>Click <strong>&#8942;</strong> menu for: view contact, assign, add coverage URL, notes, remove</li>
              <li>Use <strong>filter pills</strong> (All, Pitched, Confirmed, etc.) and <strong>sort links</strong> to organize the list</li>
            </ul>
          </div>
          <div class="p-3 bg-[#F5F3F0] rounded-lg">
            <h4 class="font-medium text-[#2D2D2D] text-sm">Press Clipping Editor</h4>
            <p class="text-xs text-[#8C8C8C] mt-1">Click "Generate Clipping" on any confirmed pitch to create a visual press clip. The two-phase editor lets you first frame the image, then add text with custom fonts, text stroke, box borders, and drop shadows. You can pick a background image from the client's DPK photos or paste any image URL. Outlet logos are auto-fetched.</p>
          </div>
          <div class="p-3 bg-[#F5F3F0] rounded-lg">
            <h4 class="font-medium text-[#2D2D2D] text-sm">Import from Previous Release</h4>
            <p class="text-xs text-[#8C8C8C] mt-1">Click "Import from Release" to copy contacts from a previous release for the same client. Duplicates are skipped.</p>
          </div>
          <div class="p-3 bg-[#F5F3F0] rounded-lg">
            <h4 class="font-medium text-[#2D2D2D] text-sm">Coverage URL Tracking</h4>
            <p class="text-xs text-[#8C8C8C] mt-1">When a pitch is confirmed, add the coverage URL to track where the article was published. Coverage URLs are visible on the contact's pitch history and can be used when generating press clips.</p>
          </div>
          <div class="p-3 bg-[#F5F3F0] rounded-lg">
            <h4 class="font-medium text-[#2D2D2D] text-sm">Conflict Detection</h4>
            <p class="text-xs text-[#8C8C8C] mt-1">The system warns you if a contact is already on another active release for the same client, preventing accidental double-pitching.</p>
          </div>
        </div>
      </div>
    `
  }

  getTasksHelp() {
    return `
      <div id="help-tasks" data-help-tab-content class="hidden space-y-4">
        <h3 class="text-lg font-semibold text-[#2D2D2D]">Tasks</h3>
        <p class="text-sm text-[#5A5A5A]">Track and manage all PR tasks and to-dos across the team.</p>
        <div class="space-y-3">
          <div class="p-3 bg-[#F5F3F0] rounded-lg">
            <h4 class="font-medium text-[#2D2D2D] text-sm">Creating Tasks</h4>
            <p class="text-xs text-[#8C8C8C] mt-1">Click "New Task" to create one. Assign to a team member, set priority and due date, optionally link to a release or contact.</p>
          </div>
          <div class="p-3 bg-[#F5F3F0] rounded-lg">
            <h4 class="font-medium text-[#2D2D2D] text-sm">Priority Levels</h4>
            <ul class="text-xs text-[#8C8C8C] mt-1 space-y-1">
              <li><span class="inline-block px-1.5 py-0.5 text-xs rounded-full bg-[#E57373]/20 text-[#E57373]">Urgent</span> / <span class="inline-block px-1.5 py-0.5 text-xs rounded-full bg-[#FFB74D]/20 text-[#FFB74D]">High</span> / <span class="inline-block px-1.5 py-0.5 text-xs rounded-full bg-[#8BA4B4]/20 text-[#8BA4B4]">Medium</span> / <span class="inline-block px-1.5 py-0.5 text-xs rounded-full bg-[#E8E4DF] text-[#8C8C8C]">Low</span></li>
            </ul>
          </div>
          <div class="p-3 bg-[#F5F3F0] rounded-lg">
            <h4 class="font-medium text-[#2D2D2D] text-sm">Cross-App Visibility</h4>
            <p class="text-xs text-[#8C8C8C] mt-1">Tasks created in any TREND app (PRT, SMT, SPT, BFT) are visible across all apps. You can filter by department to see only tasks relevant to your current workflow.</p>
          </div>
          <div class="p-3 bg-[#F5F3F0] rounded-lg">
            <h4 class="font-medium text-[#2D2D2D] text-sm">Intern Tasks</h4>
            <p class="text-xs text-[#8C8C8C] mt-1">Assign tasks to interns from the Interns page via "Assign Task". Interns see their assigned tasks on their dashboard.</p>
          </div>
        </div>
      </div>
    `
  }

  getInternsHelp() {
    return `
      <div id="help-interns" data-help-tab-content class="hidden space-y-4">
        <h3 class="text-lg font-semibold text-[#2D2D2D]">Interns</h3>
        <p class="text-sm text-[#5A5A5A]">Manage your intern team from one place.</p>
        <div class="space-y-3">
          <div class="p-3 bg-[#F5F3F0] rounded-lg">
            <h4 class="font-medium text-[#2D2D2D] text-sm">Intern List</h4>
            <p class="text-xs text-[#8C8C8C] mt-1">See all current interns with their status, assigned tasks, and activity.</p>
          </div>
          <div class="p-3 bg-[#F5F3F0] rounded-lg">
            <h4 class="font-medium text-[#2D2D2D] text-sm">Intern Profiles</h4>
            <p class="text-xs text-[#8C8C8C] mt-1">View each intern's task history, completed work, and standing assignments.</p>
          </div>
          <div class="p-3 bg-[#F5F3F0] rounded-lg">
            <h4 class="font-medium text-[#2D2D2D] text-sm">Assigning Work</h4>
            <p class="text-xs text-[#8C8C8C] mt-1">Create tasks for interns from the Interns page or from any release. Interns see their assigned tasks on their dashboard.</p>
          </div>
          <div class="p-3 bg-[#F5F3F0] rounded-lg">
            <h4 class="font-medium text-[#2D2D2D] text-sm">Permissions</h4>
            <p class="text-xs text-[#8C8C8C] mt-1">Interns have limited access. They cannot delete press clips, archive outlets globally, access settings, or manage users. A press clip an intern adds waits for the client's publicist under DPKs, in Pending Press Clips.</p>
          </div>
        </div>
      </div>
    `
  }

  getReviewQueueHelp() {
    return `
      <div id="help-reviewqueue" data-help-tab-content class="hidden space-y-4">
        <h3 class="text-lg font-semibold text-[#2D2D2D]">Review Queue</h3>
        <p class="text-sm text-[#5A5A5A]">Items waiting for a person: new outlets and email problems.</p>
        <div class="space-y-3">
          <div class="p-3 bg-[#F5F3F0] rounded-lg">
            <h4 class="font-medium text-[#2D2D2D] text-sm">Press clips from interns</h4>
            <p class="text-xs text-[#8C8C8C] mt-1">These are not in the Review Queue. A press clip an intern adds waits under DPKs, in Pending Press Clips, and the number beside DPKs in the menu is how many wait on your clients. Open one to approve it onto the DPK, or ask for changes with a note. The intern is told either way, and can fix the clip and send it back.</p>
          </div>
        </div>
      </div>
    `
  }

  getContactsHelp() {
    return `
      <div id="help-contacts" data-help-tab-content class="hidden space-y-4">
        <h3 class="text-lg font-semibold text-[#2D2D2D]">Contacts</h3>
        <p class="text-sm text-[#5A5A5A]">Manage your media contact database for pitching campaigns.</p>
        <div class="space-y-3">
          <div class="p-3 bg-[#F5F3F0] rounded-lg">
            <h4 class="font-medium text-[#2D2D2D] text-sm">Contact List</h4>
            <p class="text-xs text-[#8C8C8C] mt-1">Browse and search all media contacts. Use filters by outlet, location, or verification status.</p>
          </div>
          <div class="p-3 bg-[#F5F3F0] rounded-lg">
            <h4 class="font-medium text-[#2D2D2D] text-sm">Adding Contacts</h4>
            <p class="text-xs text-[#8C8C8C] mt-1">Click "Add Contact" to create a new one. Required: name and email. The system prevents duplicate email addresses and warns if a similar name exists.</p>
          </div>
          <div class="p-3 bg-[#F5F3F0] rounded-lg">
            <h4 class="font-medium text-[#2D2D2D] text-sm">Email Verification</h4>
            <ul class="text-xs text-[#8C8C8C] mt-1 space-y-1">
              <li><span class="inline-block px-1.5 py-0.5 text-xs rounded-full bg-[#9CAF88]/20 text-[#9CAF88]">Verified</span> - Email confirmed valid</li>
              <li><span class="inline-block px-1.5 py-0.5 text-xs rounded-full bg-[#FFB74D]/20 text-[#FFB74D]">Pending</span> - Awaiting verification</li>
              <li><span class="inline-block px-1.5 py-0.5 text-xs rounded-full bg-[#E57373]/20 text-[#E57373]">Invalid</span> - Email bounced or rejected</li>
            </ul>
          </div>
        </div>
      </div>
    `
  }

  getOutletsHelp() {
    return `
      <div id="help-outlets" data-help-tab-content class="hidden space-y-4">
        <h3 class="text-lg font-semibold text-[#2D2D2D]">Outlets</h3>
        <p class="text-sm text-[#5A5A5A]">Manage media outlets and publications in your database.</p>
        <div class="space-y-3">
          <div class="p-3 bg-[#F5F3F0] rounded-lg">
            <h4 class="font-medium text-[#2D2D2D] text-sm">Outlet Types</h4>
            <ul class="text-xs text-[#8C8C8C] mt-1 space-y-1 list-disc list-inside">
              <li>Blogs and online publications</li>
              <li>Print magazines and newspapers</li>
              <li>Radio, Podcasts, and broadcast</li>
              <li>Playlists (Spotify, Apple Music, etc.)</li>
            </ul>
          </div>
          <div class="p-3 bg-[#F5F3F0] rounded-lg">
            <h4 class="font-medium text-[#2D2D2D] text-sm">Archiving Outlets</h4>
            <p class="text-xs text-[#8C8C8C] mt-1">Archiving behavior depends on your role:</p>
            <ul class="text-xs text-[#8C8C8C] mt-1 space-y-1 list-disc list-inside">
              <li><strong>Admin / Senior Staff:</strong> Archive is <strong>global</strong>: the outlet is permanently excluded from all future AI searches for every user.</li>
              <li><strong>Intern:</strong> Archive only marks the result in that specific search. The outlet is not blocked globally.</li>
            </ul>
            <p class="text-xs text-[#8C8C8C] mt-1">Admins can manage and reverse archives from Settings &rarr; Archived Outlets, or via the "View Archived" button on this page.</p>
          </div>
          <div class="p-3 bg-[#F5F3F0] rounded-lg">
            <h4 class="font-medium text-[#2D2D2D] text-sm">UVM (Unique Visitors/Month)</h4>
            <p class="text-xs text-[#8C8C8C] mt-1">Track monthly visitor counts to prioritize high-traffic publications.</p>
          </div>
        </div>
      </div>
    `
  }

  getAiSearchHelp() {
    return `
      <div id="help-aisearch" data-help-tab-content class="hidden space-y-4">
        <h3 class="text-lg font-semibold text-[#2D2D2D]">AI Search</h3>
        <p class="text-sm text-[#5A5A5A]">Discover new media outlets using Google-powered search. Run searches standalone or from within a release.</p>
        <div class="space-y-3">
          <div class="p-3 bg-[#F5F3F0] rounded-lg">
            <h4 class="font-medium text-[#2D2D2D] text-sm">Running a Search</h4>
            <p class="text-xs text-[#8C8C8C] mt-1">Enter a search query (e.g., "indie music blogs") and click Search. Results are fetched from Google via SerpAPI, deduplicated, and checked against your existing outlet database.</p>
          </div>
          <div class="p-3 bg-[#F5F3F0] rounded-lg">
            <h4 class="font-medium text-[#2D2D2D] text-sm">Reviewing Results</h4>
            <p class="text-xs text-[#8C8C8C] mt-1">Results are split into two buckets:</p>
            <ul class="text-xs text-[#8C8C8C] mt-1 space-y-1 list-disc list-inside">
              <li><span class="text-[#8BA4B4] font-medium">Already in Database</span>: outlets that already exist in PRT. Save or add to pitch list.</li>
              <li><span class="text-[#C4A484] font-medium">Newly Found</span>: new outlets. Click to open a review modal where you can edit the name, select outlet type(s), and view/add contacts from Hunter.io.</li>
            </ul>
          </div>
          <div class="p-3 bg-[#F5F3F0] rounded-lg">
            <h4 class="font-medium text-[#2D2D2D] text-sm">Saving New Outlets</h4>
            <p class="text-xs text-[#8C8C8C] mt-1">Click a newly found outlet to open the save modal. You can edit the outlet name, select one or more types (blog, podcast, etc.), and choose which Hunter.io contacts to import. If the search is linked to a release, contacts are automatically added to the pitch list.</p>
          </div>
          <div class="p-3 bg-[#F5F3F0] rounded-lg">
            <h4 class="font-medium text-[#2D2D2D] text-sm">Cancelling a Search</h4>
            <p class="text-xs text-[#8C8C8C] mt-1">While a search is processing, click "Cancel" to stop it early. Results found so far are kept.</p>
          </div>
          <div class="p-3 bg-[#F5F3F0] rounded-lg">
            <h4 class="font-medium text-[#2D2D2D] text-sm">Archiving Outlets</h4>
            <p class="text-xs text-[#8C8C8C] mt-1">Archive permanently excludes an outlet from future searches. How it works depends on your role:</p>
            <ul class="text-xs text-[#8C8C8C] mt-1 space-y-1 list-disc list-inside">
              <li><strong>Admin / Senior Staff:</strong> Archiving is <strong>global</strong>: the outlet is blocked from all future AI searches for every user.</li>
              <li><strong>Intern:</strong> Archiving only marks that result as "archived" in that specific search. It does <strong>not</strong> block the outlet globally, so other users (including other interns) will still see it.</li>
            </ul>
            <p class="text-xs text-[#8C8C8C] mt-1">Admins can reverse any archive from Settings &rarr; Archived Outlets, or from the "View Archived" button on the Outlets page.</p>
          </div>
        </div>
      </div>
    `
  }

  getNotificationsHelp() {
    return `
      <div id="help-notifications" data-help-tab-content class="hidden space-y-4">
        <h3 class="text-lg font-semibold text-[#2D2D2D]">Notifications</h3>
        <p class="text-sm text-[#5A5A5A]">Your notification center for PRT activity. This is the bell icon in the top bar.</p>
        <div class="space-y-3">
          <div class="p-3 bg-[#F5F3F0] rounded-lg">
            <h4 class="font-medium text-[#2D2D2D] text-sm">Notification Types</h4>
            <ul class="text-xs text-[#8C8C8C] mt-1 space-y-1 list-disc list-inside">
              <li>Status changes on pitches</li>
              <li>New coverage confirmations</li>
              <li>Intern submissions pending review</li>
              <li>Task assignments</li>
              <li>Press clips approved or needing changes</li>
              <li>Team activity across your releases</li>
            </ul>
          </div>
          <div class="p-3 bg-[#F5F3F0] rounded-lg">
            <h4 class="font-medium text-[#2D2D2D] text-sm">Quick Actions</h4>
            <p class="text-xs text-[#8C8C8C] mt-1">Click any notification to jump directly to the related item. Mark notifications as read individually or in bulk.</p>
          </div>
          <div class="p-3 bg-[#F5F3F0] rounded-lg">
            <h4 class="font-medium text-[#2D2D2D] text-sm">Badge Count</h4>
            <p class="text-xs text-[#8C8C8C] mt-1">The bell icon shows your unread notification count plus any pending review queue items.</p>
          </div>
        </div>
      </div>
    `
  }

  getTeamHelp() {
    return `
      <div id="help-team" data-help-tab-content class="hidden space-y-4">
        <h3 class="text-lg font-semibold text-[#2D2D2D]">TREND Team</h3>
        <p class="text-sm text-[#5A5A5A]">View the full TREND team directory. See who's on the team, their roles across all TREND apps (PRT, SMT, SPT, BFT), and contact information. This is a read-only directory visible to all team members including interns.</p>
      </div>
    `
  }

  getFilesHelp() {
    return `
      <div id="help-files" data-help-tab-content class="hidden space-y-4">
        <h3 class="text-lg font-semibold text-[#2D2D2D]">Files</h3>
        <p class="text-sm text-[#5A5A5A]">Internal file storage for the team. Upload and organize documents, templates, and resources that the PR team needs access to. Files are shared across the team (not client-specific).</p>
      </div>
    `
  }

  getLogHistoryHelp() {
    return `
      <div id="help-loghistory" data-help-tab-content class="hidden space-y-4">
        <h3 class="text-lg font-semibold text-[#2D2D2D]">Log History</h3>
        <p class="text-sm text-[#5A5A5A]">Activity log showing all significant actions taken in PRT. Track who did what and when: contact changes, release updates, import activity, and more. Useful for auditing and understanding team activity patterns.</p>
      </div>
    `
  }

  getUsersHelp() {
    return `
      <div id="help-users" data-help-tab-content class="hidden space-y-4">
        <h3 class="text-lg font-semibold text-[#2D2D2D]">Users</h3>
        <p class="text-sm text-[#5A5A5A]">User management for PRT administrators.</p>
        <div class="space-y-3">
          <div class="p-3 bg-[#F5F3F0] rounded-lg">
            <h4 class="font-medium text-[#2D2D2D] text-sm">Roles</h4>
            <ul class="text-xs text-[#8C8C8C] mt-1 space-y-1 list-disc list-inside">
              <li><strong>Admin:</strong> Full access including settings and user management</li>
              <li><strong>Senior Staff:</strong> Full PR workflow access</li>
              <li><strong>Intern:</strong> Limited, supervised access</li>
            </ul>
            <p class="text-xs text-[#8C8C8C] mt-2">Each user can have different roles across different TREND apps. For example, someone might be Admin in PRT but Senior Staff in SMT.</p>
          </div>
          <div class="p-3 bg-[#F5F3F0] rounded-lg">
            <h4 class="font-medium text-[#2D2D2D] text-sm">Access Control</h4>
            <p class="text-xs text-[#8C8C8C] mt-1">Toggle which TREND apps each user can access. Removing app access automatically clears their role for that app.</p>
          </div>
        </div>
      </div>
    `
  }

  getImportHelp() {
    return `
      <div id="help-import" data-help-tab-content class="hidden space-y-4">
        <h3 class="text-lg font-semibold text-[#2D2D2D]">CSV Import</h3>
        <p class="text-sm text-[#5A5A5A]">Bulk import contacts from CSV or Excel-exported spreadsheets. Accessed from within a release or from the Import page.</p>
        <div class="space-y-3">
          <div class="p-3 bg-[#F5F3F0] rounded-lg">
            <h4 class="font-medium text-[#2D2D2D] text-sm">How to Import</h4>
            <ol class="text-xs text-[#8C8C8C] mt-1 space-y-1 list-decimal list-inside">
              <li>Go to <strong>Import</strong> in the sidebar or from within a release</li>
              <li>Upload a CSV file</li>
              <li>Optionally link to a release</li>
              <li>Choose update vs. skip for duplicates</li>
              <li>Click Import and review results</li>
            </ol>
          </div>
          <div class="p-3 bg-[#F5F3F0] rounded-lg">
            <h4 class="font-medium text-[#2D2D2D] text-sm">Supported Columns</h4>
            <p class="text-xs text-[#8C8C8C] mt-1">Auto-detected: outlet, contact/name, first name, last name, email, feature type, genre, uvm, city/location, website, notes</p>
          </div>
          <div class="p-3 bg-[#F5F3F0] rounded-lg">
            <h4 class="font-medium text-[#2D2D2D] text-sm">Deduplication</h4>
            <p class="text-xs text-[#8C8C8C] mt-1">Contacts matched by email. Existing contacts skipped or updated based on your choice.</p>
          </div>
        </div>
      </div>
    `
  }

  getProfileHelp() {
    return `
      <div id="help-profile" data-help-tab-content class="hidden space-y-4">
        <h3 class="text-lg font-semibold text-[#2D2D2D]">Your Profile</h3>
        <p class="text-sm text-[#5A5A5A]">Manage your account settings and preferences.</p>
        <div class="space-y-3">
          <div class="p-3 bg-[#F5F3F0] rounded-lg">
            <h4 class="font-medium text-[#2D2D2D] text-sm">Account Roles</h4>
            <ul class="text-xs text-[#8C8C8C] mt-1 space-y-1 list-disc list-inside">
              <li><strong>Admin:</strong> Full system access, user management, settings</li>
              <li><strong>Senior Staff:</strong> Full PR workflow (contacts, releases, import, archive)</li>
              <li><strong>Intern:</strong> Limited access for assigned tasks</li>
            </ul>
          </div>
        </div>
      </div>
    `
  }

  getSettingsHelp() {
    return `
      <div id="help-settings" data-help-tab-content class="hidden space-y-4">
        <h3 class="text-lg font-semibold text-[#2D2D2D]">Settings / Master Config</h3>
        <p class="text-sm text-[#5A5A5A]">Configure system settings, API integrations, and archive management. (Admin only)</p>
        <div class="space-y-3">
          <div class="p-3 bg-[#F5F3F0] rounded-lg">
            <h4 class="font-medium text-[#2D2D2D] text-sm">Setting Groups</h4>
            <ul class="text-xs text-[#8C8C8C] mt-1 space-y-1 list-disc list-inside">
              <li><strong>Discovery:</strong> Google search time range, results count, excluded domains</li>
              <li><strong>Hype Machine:</strong> Auto-refresh interval for blog database</li>
              <li><strong>Hunter.io:</strong> Email verification and auto-find</li>
              <li><strong>UVM:</strong> Visitor tracking source and refresh intervals</li>
              <li><strong>General:</strong> Conflict warnings, activity thresholds, import defaults</li>
            </ul>
          </div>
          <div class="p-3 bg-[#F5F3F0] rounded-lg">
            <h4 class="font-medium text-[#2D2D2D] text-sm">Archived Outlets</h4>
            <p class="text-xs text-[#8C8C8C] mt-1">View and manage globally archived outlets. Archived outlets are excluded from discovery results. Un-archive from here or from the outlet page.</p>
          </div>
          <div class="p-3 bg-[#F5F3F0] rounded-lg">
            <h4 class="font-medium text-[#2D2D2D] text-sm">API Status & Webhooks</h4>
            <p class="text-xs text-[#8C8C8C] mt-1">See which services are configured (SerpAPI, Spotify, Hunter.io, AWS S3) and copy webhook URLs for external integrations.</p>
          </div>
        </div>
      </div>
    `
  }

  getShortcutsHelp() {
    return `
      <div id="help-shortcuts" data-help-tab-content class="hidden space-y-4">
        <h3 class="text-lg font-semibold text-[#2D2D2D]">Keyboard Shortcuts</h3>
        <div class="p-3 bg-[#F5F3F0] rounded-lg">
          <table class="w-full text-xs mt-1">
            <tbody class="divide-y divide-[#E8E4DF]">
              <tr><td class="py-1.5 text-[#8C8C8C]"><kbd class="px-1.5 py-0.5 bg-white rounded border border-[#E8E4DF]">Cmd/Ctrl</kbd> + <kbd class="px-1.5 py-0.5 bg-white rounded border border-[#E8E4DF]">K</kbd></td><td class="py-1.5 text-[#2D2D2D]">Open help panel</td></tr>
              <tr><td class="py-1.5 text-[#8C8C8C]"><kbd class="px-1.5 py-0.5 bg-white rounded border border-[#E8E4DF]">Esc</kbd></td><td class="py-1.5 text-[#2D2D2D]">Close panels and modals</td></tr>
            </tbody>
          </table>
        </div>
      </div>
    `
  }

  // ── Icons ──

  iconGrid() { return `<svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M4 6a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2H6a2 2 0 01-2-2V6zM14 6a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2h-2a2 2 0 01-2-2V6zM4 16a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2H6a2 2 0 01-2-2v-2zM14 16a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2h-2a2 2 0 01-2-2v-2z"/></svg>` }
  iconCalendar() { return `<svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z"/></svg>` }
  iconInbox() { return `<svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M20 13V6a2 2 0 00-2-2H6a2 2 0 00-2 2v7m16 0v5a2 2 0 01-2 2H6a2 2 0 01-2-2v-5m16 0h-2.586a1 1 0 00-.707.293l-2.414 2.414a1 1 0 01-.707.293h-3.172a1 1 0 01-.707-.293l-2.414-2.414A1 1 0 006.586 13H4"/></svg>` }
  iconClients() { return `<svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z"/></svg>` }
  iconDpk() { return `<svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M19 11H5m14 0a2 2 0 012 2v6a2 2 0 01-2 2H5a2 2 0 01-2-2v-6a2 2 0 012-2m14 0V9a2 2 0 00-2-2M5 11V9a2 2 0 012-2m0 0V5a2 2 0 012-2h6a2 2 0 012 2v2M7 7h10"/></svg>` }
  iconReleases() { return `<svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 19V6l12-3v13M9 19c0 1.105-1.343 2-3 2s-3-.895-3-2 1.343-2 3-2 3 .895 3 2zm12-3c0 1.105-1.343 2-3 2s-3-.895-3-2 1.343-2 3-2 3 .895 3 2zM9 10l12-3"/></svg>` }
  iconTasks() { return `<svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2m-6 9l2 2 4-4"/></svg>` }
  iconInterns() { return `<svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 4.354a4 4 0 110 5.292M15 21H3v-1a6 6 0 0112 0v1zm0 0h6v-1a6 6 0 00-9-5.197M13 7a4 4 0 11-8 0 4 4 0 018 0z"/></svg>` }
  iconReviewQueue() { return `<svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2"/></svg>` }
  iconContacts() { return `<svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z"/></svg>` }
  iconOutlets() { return `<svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M19 20H5a2 2 0 01-2-2V6a2 2 0 012-2h10a2 2 0 012 2v1m2 13a2 2 0 01-2-2V7m2 13a2 2 0 002-2V9a2 2 0 00-2-2h-2m-4-3H9M7 16h6M7 8h6v4H7V8z"/></svg>` }
  iconAiSearch() { return `<svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z"/></svg>` }
  iconNotifications() { return `<svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M15 17h5l-1.405-1.405A2.032 2.032 0 0118 14.158V11a6.002 6.002 0 00-4-5.659V5a2 2 0 10-4 0v.341C7.67 6.165 6 8.388 6 11v3.159c0 .538-.214 1.055-.595 1.436L4 17h5m6 0v1a3 3 0 11-6 0v-1m6 0H9"/></svg>` }
  iconTeam() { return `<svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z"/></svg>` }
  iconFiles() { return `<svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M3 7v10a2 2 0 002 2h14a2 2 0 002-2V9a2 2 0 00-2-2h-6l-2-2H5a2 2 0 00-2 2z"/></svg>` }
  iconLogs() { return `<svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z"/></svg>` }
  iconUsers() { return `<svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 4.354a4 4 0 110 5.292M15 21H3v-1a6 6 0 0112 0v1zm0 0h6v-1a6 6 0 00-9-5.197M13 7a4 4 0 11-8 0 4 4 0 018 0z"/></svg>` }
  iconImport() { return `<svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-8l-4-4m0 0L8 8m4-4v12"/></svg>` }
  iconProfile() { return `<svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z"/></svg>` }
  iconSettings() { return `<svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M10.325 4.317c.426-1.756 2.924-1.756 3.35 0a1.724 1.724 0 002.573 1.066c1.543-.94 3.31.826 2.37 2.37a1.724 1.724 0 001.066 2.573c1.756.426 1.756 2.924 0 3.35a1.724 1.724 0 00-1.066 2.573c.94 1.543-.826 3.31-2.37 2.37a1.724 1.724 0 00-2.573 1.066c-.426 1.756-2.924 1.756-3.35 0a1.724 1.724 0 00-2.573-1.066c-1.543.94-3.31-.826-2.37-2.37a1.724 1.724 0 00-1.066-2.573c-1.756-.426-1.756-2.924 0-3.35a1.724 1.724 0 001.066-2.573c-.94-1.543.826-3.31 2.37-2.37.996.608 2.296.07 2.572-1.065z"/><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M15 12a3 3 0 11-6 0 3 3 0 016 0z"/></svg>` }
  iconShortcuts() { return `<svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 3v1m0 16v1m9-9h-1M4 12H3m15.364 6.364l-.707-.707M6.343 6.343l-.707-.707m12.728 0l-.707.707M6.343 17.657l-.707.707M16 12a4 4 0 11-8 0 4 4 0 018 0z"/></svg>` }
}

window.showHelpTab = function(tabId) {
  const contents = document.querySelectorAll("[data-help-tab-content]")
  contents.forEach(c => c.classList.add("hidden"))
  const selectedContent = document.getElementById(`help-${tabId}`)
  if (selectedContent) selectedContent.classList.remove("hidden")
  const buttons = document.querySelectorAll("[data-help-tab]")
  buttons.forEach(btn => {
    const isSelected = btn.dataset.helpTab === tabId
    btn.className = isSelected
      ? "flex items-center px-3 py-2 text-sm font-medium text-[#C4A484] bg-[#C4A484]/10 rounded-lg"
      : "flex items-center px-3 py-2 text-sm text-[#8C8C8C] hover:bg-[#F5F3F0] rounded-lg transition-colors"
  })
}
